import { useState, useMemo, useEffect } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer,
} from 'recharts';
import { X } from 'lucide-react';
import Button from '../../../components/Button/Button';
import RiskLevelGuide from '../../../components/RiskLevelGuide/RiskLevelGuide';
import RiskSlider from '../../../components/RiskSlider/RiskSlider';
import { levelsFor, levelById, multFor } from './riskLevels';
import styles from './StartCopying.module.css';

/* The three steps, in order. Back walks this list, so there is one definition of
   what "the previous step" means. */
const STEPS = ['account', 'risk', 'confirm'];

const STEP_TITLES = {
  account: 'Select a slave account',
  risk:    'Select risk tolerance',
  confirm: 'Confirm settings',
};

const STATUS_LABELS = {
  available: 'Available',
  connected: 'Connected',
  settingup: 'Setting up',
};

/* ── Projection chart ──
   Thirteen monthly points from today to a year out. The good year is a rising
   curve, the bad year is that same curve reflected through the starting line —
   the design draws the pair as mirror images — and the typical year runs between
   them at a fraction of the slope.

   `spread` scales with the risk level, and the Y axis is deliberately NOT fitted
   to the data: every level is drawn against the same fixed domain, which is what
   makes a more aggressive setting visibly reach further up and down the box
   rather than redrawing the same picture at a different scale. */
const CHART_POINTS = 13;
const CHART_DOMAIN = 1; // the axis runs -1..1; a level's spread is a share of it

// Hand-drawn wobble rather than a clean arc: a fixed sequence, so the line is
// the same shape every time a level is selected.
const WOBBLE = [0, 0.06, 0.02, 0.05, 0.09, 0.07, 0.04, 0.07, 0.03, 0.06, 0.02, 0.04, 0];

function projection(spread) {
  return Array.from({ length: CHART_POINTS }, (_, i) => {
    const t = i / (CHART_POINTS - 1);
    // Eased rather than linear, so the year opens gradually and keeps climbing.
    const path = (t * 0.55 + t * t * 0.45 + WOBBLE[i] * t) * spread;
    return {
      month: i,
      good: path,
      bad: -path,
      typical: path * 0.3,
    };
  });
}

/* How far up the box each level's good year reaches. Conservative uses a third
   of the height, Aggressive nearly all of it. */
const SPREAD_BY_LEVEL = {
  conservative: 0.34,
  standard:     0.52,
  moderate:     0.74,
  aggressive:   0.92,
};

const TICKS = { 0: 'Today', 6: 'In 6 months', 12: 'In 1 year' };

/* The outer labels sit against the ends of the axis, so centring them on their
   own tick would hang half of each one outside the chart. First anchors left,
   last anchors right, the middle one stays centred. */
function AxisTick({ x, y, payload, index, fill }) {
  const anchor = index === 0 ? 'start' : index === 2 ? 'end' : 'middle';
  return (
    <text x={x} y={y + 12} fill={fill} fontSize={12} textAnchor={anchor}>
      {TICKS[payload.value] || ''}
    </text>
  );
}

function useIsDark() {
  const check = () => {
    const attr = document.documentElement.getAttribute('data-theme');
    return attr === 'dark' || (attr !== 'light' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  };
  const [dark, setDark] = useState(check);
  useEffect(() => {
    const obs = new MutationObserver(() => setDark(check()));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => obs.disconnect();
  }, []);
  return dark;
}

function ProjectionChart({ levelId }) {
  const isDark = useIsDark();
  const data = useMemo(() => projection(SPREAD_BY_LEVEL[levelId] ?? 0.5), [levelId]);
  // The typical year is the neutral line of the three: ink on the light theme,
  // paper on the dark one.
  const typicalColor = isDark ? '#FFFFFF' : '#111111';

  return (
    <div className={styles.chartCard}>
      <div className={styles.legend}>
        <span className={styles.legendItem}>
          <span className={styles.legendDash} style={{ background: '#D97070' }} />
          Bad year
        </span>
        <span className={styles.legendItem}>
          <span className={styles.legendDash} style={{ background: typicalColor }} />
          Typical year
        </span>
        <span className={styles.legendItem}>
          <span className={styles.legendDash} style={{ background: '#83C35F' }} />
          Good year
        </span>
      </div>

      <ResponsiveContainer width="100%" height={210}>
        <LineChart data={data} margin={{ top: 6, right: 6, left: 6, bottom: 0 }}>
          <CartesianGrid vertical={false} strokeDasharray="3 4" stroke={isDark ? '#384040' : '#E7E8E8'} />
          <XAxis
            dataKey="month"
            ticks={[0, 6, 12]}
            tickLine={false}
            axisLine={false}
            tick={<AxisTick fill={isDark ? '#B0B5B5' : '#525C5C'} />}
            interval={0}
            height={26}
          />
          <YAxis hide domain={[-CHART_DOMAIN, CHART_DOMAIN]} />
          <Line type="monotone" dataKey="bad" stroke="#D97070" strokeWidth={2} dot={false} isAnimationActive={false} />
          <Line type="monotone" dataKey="typical" stroke={typicalColor} strokeWidth={2} dot={false} isAnimationActive={false} />
          <Line type="monotone" dataKey="good" stroke="#83C35F" strokeWidth={2} dot={false} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

/**
 * Start copying: pick an account, pick a risk tolerance, confirm.
 *
 * The journey owns its own selection and hands the result back once — `onConfirm`
 * is called with the chosen account and the multiplier its risk level maps to,
 * and nothing is connected until then. Closing at any step throws the selection
 * away.
 */
function StartCopying({ master, slaves, slavesLoading, loading, error, onClose, onConfirm }) {
  const [step, setStep] = useState('account');
  const [slaveId, setSlaveId] = useState(null);
  const [levelId, setLevelId] = useState(null);
  const [guideOpen, setGuideOpen] = useState(false);

  const slave = slaves.find((s) => s.id === slaveId) || null;

  /* Which levels the chosen account may pick from — Conservative is a $200K
     option, so the list can only be worked out once an account is chosen. */
  const levels = useMemo(() => levelsFor(slave?.deposit), [slave]);
  const level = levelById(levelId);

  /* The default is the first level the account is entitled to, and it is reset
     whenever the account changes: a level the previous account could pick may
     not exist for this one. */
  useEffect(() => {
    if (!slave) return;
    setLevelId((current) => (levels.some((l) => l.id === current) ? current : levels[0]?.id ?? null));
  }, [slave, levels]);

  const goBack = () => setStep(STEPS[Math.max(0, STEPS.indexOf(step) - 1)]);
  const goNext = () => setStep(STEPS[Math.min(STEPS.length - 1, STEPS.indexOf(step) + 1)]);

  const masterStats = [
    master?.winRate != null ? `${String(master.winRate).replace('%', '')}% win rate` : null,
    master?.growth != null ? `${String(master.growth).replace('%', '')}% growth` : null,
    // The masters payload carries the age both ways — as a number and
    // pre-formatted ("8mo") — and the card this opens from may only have the
    // second. Either renders.
    master?.trackMonths != null ? `${master.trackMonths} mo` : (master?.track || null),
  ].filter(Boolean).join(' · ');

  return (
    <>
      <div className={styles.overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
        <div className={styles.modal} role="dialog" aria-modal="true" aria-label={STEP_TITLES[step]}>
          <div className={styles.header}>
            <h2 className={styles.title}>{STEP_TITLES[step]}</h2>
            {/* Leaves the journey from any step, connecting nothing. */}
            <button className={styles.closeBtn} onClick={onClose} aria-label="Close">
              <X size={20} />
            </button>
          </div>

          {/* ── Step 1: the account to connect ── */}
          {step === 'account' && (
            <>
              <div className={styles.body}>
                <p className={styles.sectionLabel}>CHOOSE A SLAVE ACCOUNT</p>
                <div className={styles.slaveList}>
                  {slavesLoading ? (
                    <p className={styles.hint}>Loading your accounts…</p>
                  ) : slaves.length === 0 ? (
                    <p className={styles.hint}>No deployed DFY accounts available.</p>
                  ) : slaves.map((s) => {
                    // Only an unconnected account can be connected; the others
                    // are listed so the holder can see where they stand.
                    const selectable = s.slaveStatus === 'available';
                    return (
                      <button
                        key={s.id}
                        type="button"
                        className={`${styles.slaveRow} ${slaveId === s.id ? styles.slaveRowOn : ''} ${selectable ? '' : styles.slaveRowOff}`}
                        onClick={() => selectable && setSlaveId(s.id)}
                        disabled={!selectable}
                      >
                        <span className={styles.slaveName}>{s.name}</span>
                        <span className={`${styles.pill} ${styles[`pill_${s.slaveStatus}`]}`}>
                          <span className={`${styles.dot} ${styles[`dot_${s.slaveStatus}`]}`} />
                          {STATUS_LABELS[s.slaveStatus] || s.slaveStatus}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className={styles.footer}>
                <span className={styles.footerStart} />
                <div className={styles.actions}>
                  <Button variant="secondary" size="sm" fullWidth={false} className={styles.actionBtn} onClick={onClose}>
                    Cancel
                  </Button>
                  <Button
                    variant="primary" size="sm" fullWidth={false} className={styles.actionBtn}
                    // disabled={!slaveId}
                    onClick={goNext}
                  >
                    Continue
                  </Button>
                </div>
              </div>
            </>
          )}

          {/* ── Step 2: how large the copied trades should be ── */}
          {step === 'risk' && (
            <>
              <div className={styles.body}>
                <p className={styles.sectionLabel}>RISK TOLERANCE</p>

                <RiskSlider levels={levels} value={levelId} onChange={setLevelId} />

                <ProjectionChart levelId={levelId} />

                {level && (
                  <div className={styles.summary}>
                    <p className={styles.summaryName}>{level.label}</p>
                    <p className={styles.summaryLine}>
                      Targets <strong>{level.dailyTarget}</strong> with a max daily loss of{' '}
                      <strong>{level.maxDailyLoss}</strong>.
                    </p>
                    <p className={styles.summaryLine}>{level.blurb}</p>
                  </div>
                )}
              </div>

              {/* The one footer carrying a link as well as the actions — on a
                  phone that pair is laid out differently from the other steps,
                  which the modifier selects. */}
              <div className={`${styles.footer} ${styles.footerRisk}`}>
                <button type="button" className={styles.guideLink} onClick={() => setGuideOpen(true)}>
                  Risk level guide
                </button>
                <div className={styles.actions}>
                  <Button variant="secondary" size="sm" fullWidth={false} className={styles.actionBtn} onClick={goBack}>
                    Back
                  </Button>
                  <Button
                    variant="primary" size="sm" fullWidth={false} className={styles.actionBtn}
                    disabled={!level}
                    onClick={goNext}
                  >
                    Continue
                  </Button>
                </div>
              </div>
            </>
          )}

          {/* ── Step 3: what is about to happen ── */}
          {step === 'confirm' && (
            <>
              <div className={styles.body}>
                <p className={styles.sectionLabel}>FOLLOWING MASTER</p>
                <p className={styles.masterName}>{master?.name}</p>
                {masterStats && <p className={styles.masterStats}>{masterStats}</p>}

                <p className={`${styles.sectionLabel} ${styles.sectionLabelGap}`}>RISK TOLERANCE</p>
                <p className={styles.masterName}>{level?.label}</p>

                <div className={styles.disclosure}>
                  <p className={styles.disclosureTitle}>Disclosure</p>
                  <p className={styles.disclosureText}>
                    Your selected multiplier directly affects the size of trades copied to your account.
                  </p>
                  <ul className={styles.disclosureList}>
                    <li>
                      Higher multipliers increase both potential profits and losses. Your results may
                      differ from the master due to slippage and execution.
                    </li>
                    <li>You may request an adjustment at any time.</li>
                    <li>Past performance does not guarantee future results.</li>
                  </ul>
                </div>

                {error && <p className={styles.error}>{error}</p>}
              </div>

              <div className={styles.footer}>
                <span className={styles.footerStart} />
                <div className={styles.actions}>
                  <Button
                    variant="secondary" size="sm" fullWidth={false} className={styles.actionBtn}
                    onClick={goBack} disabled={loading}
                  >
                    Back
                  </Button>
                  <Button
                    variant="primary" size="sm" fullWidth={false} className={styles.actionBtn}
                    disabled={loading || !slave || !level}
                    /* The multiplier is the level read against this account's
                       starting balance, not a fixed property of the level. */
                    onClick={() => onConfirm({ slaveId: slave.id, riskValue: multFor(level.id, slave.deposit), level })}
                  >
                    {loading ? 'Connecting…' : 'I understand, confirm'}
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Over the journey rather than in place of it, so going back leaves the
          selection exactly as it was. */}
      {guideOpen && <RiskLevelGuide onBack={() => setGuideOpen(false)} onClose={onClose} />}
    </>
  );
}

export default StartCopying;
